import React, { useEffect, useState } from 'react';
import { Clock, ChevronRight, AlertCircle, CheckCircle2, Package, Truck, Globe, Smartphone, User, XCircle, Search, Hash, Phone, Calendar, ShieldCheck } from 'lucide-react';
import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';
function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}
export type OrderStatus = 'Pending' | 'Accepted' | 'Picking' | 'Searching for Driver' | 'Awaiting Partner Courier' | 'Driver Assigned' | 'Out for Delivery' | 'Delivered' | 'Delivery Attempt Failed' | 'Held' | 'Cancelled' | 'Rejected' | 'Reassigned' | 'Awaiting Payment' | 'Ready for Pickup' | 'Return Requested' | 'Waiting Return Confirmation' | 'Refunded';
export type Channel = 'Website' | 'Aggregator' | 'Yaser eRx' | 'Walk-in' | 'Insurance Platform';
export type ChannelPartner = 'Jahez' | 'HungerStation' | 'Yaser' | 'Waseel' | 'Nafees';
export type PayerType = 'Cash' | 'Insurance' | 'Partner Prepaid' | 'Deferred';
export type PaymentType = 'COD' | 'Visa' | 'Mada' | 'ApplePay' | 'Tamara' | 'Tabby' | 'Prepaid';
export type LineStatus = 'Pending' | 'Picked' | 'Partially Picked' | 'Unavailable';
export interface LotNumber {
  number: string;
  expiryDate: string;
  stock: number;
}
export interface OrderLine {
  itemId: string;
  id: string;
  barcode: string;
  tradeDrugCode: string;
  sku: string;
  genericCode: string;
  qty: number;
  quantity: number;
  uom: string;
  unitPrice: number;
  requiresLot: boolean;
  allowSubstitution: boolean;
  instructionsAr?: string;
  pickedQuantity: number;
  dispensedTradeDrugCode?: string;
  selectedLot?: string;
  selectedLotNumber?: string;
  status: LineStatus;
  name: string;
  image: string;
  lotNumbers?: LotNumber[];
  isSerialized: boolean;
  serialNumbers: string[];
  requiresPrescription?: boolean;
}
export interface Customer {
  name: string;
  mobile: string;
  nationalId?: string;
  gender?: 'Male' | 'Female';
  dateOfBirth?: string;
}
export interface Address {
  line1: string;
  city: string;
  district: string;
  zipCode?: string;
  nationalShortAddress?: string;
}
export interface InsuranceDetails {
  payerName: string;
  policyNo: string;
  memberId: string;
  approvalNo?: string;
  copayPercent: number;
  patientShare: number;
  insuranceShare: number;
  paymentLinkStatus: 'Pending' | 'Paid' | 'Expired';
}
export interface PrescriptionActivity {
  genericCode: string;
  instructionsAr: string;
  durationDays: number;
}
export interface PrescriptionDetails {
  erxReference: string;
  activities: PrescriptionActivity[];
  acceptableTradeDrugs: Record<string, string[]>;
  verified?: boolean;
  image?: string;
}
export interface DriverInfo {
  name: string;
  phone: string;
  vehiclePlate: string;
  rating: number;
  avatar?: string;
}
export interface HistoryEvent {
  id: string;
  timestamp: string;
  status: OrderStatus;
  message: string;
  actor: 'Pharmacist' | 'System' | 'Driver' | 'Customer Service';
  actorName: string;
}
export interface BranchRouting {
  branchName: string;
  timestamp: string;
  reason?: string;
}
export interface FulfillmentProfile {
  kind: 'Counter Pickup' | 'Standard Delivery' | 'Partner Courier' | 'Insurance Settlement' | 'eRx Fulfillment' | 'Yaser Pickup';
  requiresPickupOtp: boolean;
  requiresNationalIdVerification: boolean;
  usesPartnerCourier: boolean;
  requiresPaymentLink: boolean;
  requiresPatientShareCollection: boolean;
  requiresPrescriptionReview: boolean;
  allowsPartialFulfillment: boolean;
  requiresExplicitPartialConfirmation: boolean;
  nextReadyStatus: OrderStatus;
}
export interface Order {
  id: string;
  customerName: string;
  customerPhone: string;
  customer: Customer;
  address: Address;
  lines: OrderLine[];
  itemsCount: number;
  totalAmount: number;
  status: OrderStatus;
  channel: Channel;
  channelPartner?: ChannelPartner;
  payerType: PayerType;
  paymentType: PaymentType;
  createdAt: string;
  slaMinutes: number;
  acceptanceDeadline: string;
  priority: 'High' | 'Medium' | 'Low';
  insurance?: InsuranceDetails;
  prescription?: PrescriptionDetails;
  rejectionReason?: string;
  holdReason?: string;
  returnReason?: string;
  driverInfo?: DriverInfo;
  courierName?: string;
  courierPhone?: string;
  discountAmount?: number;
  couponCode?: string;
  isLoyaltyCustomer?: boolean;
  loyaltyPointsUsed?: number;
  qitafAmount?: number;
  walletAmount?: number;
  otp?: string;
  otpVerified?: boolean;
  pickupVerification?: 'otp' | 'nationalId';
  sourceOrderId?: string;
  previousStatus?: OrderStatus;
  routingHistory?: BranchRouting[];
  currentBranch?: string;
  history: HistoryEvent[];
}
export function deriveFulfillmentProfile(order: Pick<Order, 'channel' | 'payerType' | 'pickupVerification'>): FulfillmentProfile {
  if (order.pickupVerification === 'nationalId') {
    return {
      kind: 'Yaser Pickup',
      requiresPickupOtp: false,
      requiresNationalIdVerification: true,
      usesPartnerCourier: false,
      requiresPaymentLink: false,
      requiresPatientShareCollection: false,
      requiresPrescriptionReview: true,
      allowsPartialFulfillment: false,
      requiresExplicitPartialConfirmation: false,
      nextReadyStatus: 'Ready for Pickup'
    };
  }
  if (order.channel === 'Walk-in') {
    return {
      kind: 'Counter Pickup',
      requiresPickupOtp: true,
      requiresNationalIdVerification: false,
      usesPartnerCourier: false,
      requiresPaymentLink: false,
      requiresPatientShareCollection: order.payerType === 'Insurance',
      requiresPrescriptionReview: false,
      allowsPartialFulfillment: true,
      requiresExplicitPartialConfirmation: false,
      nextReadyStatus: 'Ready for Pickup'
    };
  }
  if (order.payerType === 'Insurance' || order.channel === 'Insurance Platform') {
    return {
      kind: 'Insurance Settlement',
      requiresPickupOtp: false,
      requiresNationalIdVerification: false,
      usesPartnerCourier: false,
      requiresPaymentLink: true,
      requiresPatientShareCollection: false,
      requiresPrescriptionReview: false,
      allowsPartialFulfillment: true,
      requiresExplicitPartialConfirmation: false,
      nextReadyStatus: 'Searching for Driver'
    };
  }
  if (order.channel === 'Yaser eRx') {
    return {
      kind: 'eRx Fulfillment',
      requiresPickupOtp: false,
      requiresNationalIdVerification: false,
      usesPartnerCourier: true,
      requiresPaymentLink: false,
      requiresPatientShareCollection: false,
      requiresPrescriptionReview: true,
      allowsPartialFulfillment: false,
      requiresExplicitPartialConfirmation: false,
      nextReadyStatus: 'Awaiting Partner Courier'
    };
  }
  if (order.channel === 'Aggregator' || order.payerType === 'Partner Prepaid') {
    return {
      kind: 'Partner Courier',
      requiresPickupOtp: false,
      requiresNationalIdVerification: false,
      usesPartnerCourier: true,
      requiresPaymentLink: false,
      requiresPatientShareCollection: false,
      requiresPrescriptionReview: false,
      allowsPartialFulfillment: false,
      requiresExplicitPartialConfirmation: false,
      nextReadyStatus: 'Awaiting Partner Courier'
    };
  }
  return {
    kind: 'Standard Delivery',
    requiresPickupOtp: false,
    requiresNationalIdVerification: false,
    usesPartnerCourier: false,
    requiresPaymentLink: order.payerType === 'Deferred',
    requiresPatientShareCollection: false,
    requiresPrescriptionReview: false,
    allowsPartialFulfillment: true,
    requiresExplicitPartialConfirmation: order.channel === 'Website',
    nextReadyStatus: 'Searching for Driver'
  };
}
interface OrderQueueProps {
  orders: Order[];
  onSelectOrder: (order: Order) => void;
}
const channels: Channel[] = ['Website', 'Aggregator', 'Yaser eRx', 'Walk-in', 'Insurance Platform'];
export function OrderQueue({
  orders,
  onSelectOrder
}: OrderQueueProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [phoneQuery, setPhoneQuery] = useState('');
  const [sourceFilter, setSourceFilter] = useState<string>('all');
  const [dateFilter, setDateFilter] = useState('');
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, []);
  const filteredOrders = orders.filter(order => {
    const belongsInHistory = ['Delivered', 'Cancelled', 'Rejected', 'Reassigned', 'Return Requested', 'Refunded'].includes(order.status);
    if (belongsInHistory) return false;
    const matchesSearch = order.id.toLowerCase().includes(searchQuery.toLowerCase()) || order.customer.name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesPhone = order.customer.mobile.includes(phoneQuery) || !phoneQuery;
    const matchesSource = sourceFilter === 'all' || order.channel === sourceFilter;
    const matchesDate = !dateFilter || order.createdAt.includes(dateFilter);
    return matchesSearch && matchesPhone && matchesSource && matchesDate;
  });
  return <div className="space-y-6" data-id="element-678" data-mp="m1ihr4hoo3fc">
      <div className="flex items-center justify-between" data-id="element-679" data-mp="zyyqwnrd6bas">
        <div data-id="element-680" data-mp="100i7ygkugqdm">
          <h2 className="text-2xl font-bold text-slate-900" data-id="element-681" data-mp="zwubb8k3rnuo">Order Queue</h2>
          <p className="text-slate-500" data-id="element-682" data-mp="uyki33hxuxbp">Manage incoming orders and fulfillment SLAs</p>
        </div>
      </div>

      <div className="grid grid-cols-4 gap-4 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm" data-id="element-683" data-mp="19va3leuv935r">
        <div className="space-y-2" data-id="element-684" data-mp="14w76cgl4o18c">
          <label className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-slate-500" data-id="element-685" data-mp="1iq7yuslhks09"><Hash className="h-3 w-3" data-id="element-686" data-mp="1enl5uod2x11h" />Order ID / Name</label>
          <div className="relative" data-id="element-687" data-mp="1eq7jn5qkccs5">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" data-id="element-688" data-mp="1sncletj8uphl" />
            <input type="text" value={searchQuery} onChange={event => setSearchQuery(event.target.value)} placeholder="Search ID or Name..." className="w-full rounded-xl border-2 border-transparent bg-slate-50 py-2 pl-10 pr-4 text-sm outline-none transition-colors focus:border-blue-500 focus:bg-white" data-id="element-689" data-mp="1npb4ae5dp08r" />
          </div>
        </div>
        <div className="space-y-2" data-id="element-690" data-mp="2tiqgyygh1dj0">
          <label className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-slate-500" data-id="element-691" data-mp="2oj45atgak5cb"><Phone className="h-3 w-3" data-id="element-692" data-mp="3l9egy76escv3" />Customer Mobile</label>
          <div className="relative" data-id="element-693" data-mp="3l8uzssol1si4">
            <Phone className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" data-id="element-694" data-mp="3h7t7ywwzwiq6" />
            <input type="text" value={phoneQuery} onChange={event => setPhoneQuery(event.target.value)} placeholder="9665XXXXXXXX" className="w-full rounded-xl border-2 border-transparent bg-slate-50 py-2 pl-10 pr-4 text-sm outline-none transition-colors focus:border-blue-500 focus:bg-white" data-id="element-695" data-mp="3c8q3peouagj9" />
          </div>
        </div>
        <div className="space-y-2" data-id="element-696" data-mp="1vtx4fntd73nw">
          <label className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-slate-500" data-id="element-697" data-mp="1vsvlkx63xcvm"><Globe className="h-3 w-3" data-id="element-698" data-mp="1ls30t7dq0q8n" />Source</label>
          <select value={sourceFilter} onChange={event => setSourceFilter(event.target.value)} className="w-full appearance-none rounded-xl border-2 border-transparent bg-slate-50 px-4 py-2 text-sm outline-none transition-colors focus:border-blue-500 focus:bg-white" data-id="element-699" data-mp="1qp35peyu3my6">
            <option value="all" data-id="element-700" data-mp="20h8xoop364dd">All Sources</option>
            {channels.map(channel => <option key={channel} value={channel} data-id="element-701" data-mp="24grhfh5689em">{channel}</option>)}
          </select>
        </div>
        <div className="space-y-2" data-id="element-702" data-mp="29e9i6k91bibj">
          <label className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-slate-500" data-id="element-703" data-mp="2efgosfzfybgm"><Calendar className="h-3 w-3" data-id="element-704" data-mp="2je0acfa4hl8g" />Date</label>
          <input type="date" value={dateFilter} onChange={event => setDateFilter(event.target.value)} className="w-full rounded-xl border-2 border-transparent bg-slate-50 px-4 py-2 text-sm outline-none transition-colors focus:border-blue-500 focus:bg-white" data-id="element-705" data-mp="2jds0yxd7ky20" />
        </div>
      </div>

      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm" data-id="element-706" data-mp="h0cmi4ym0uy7">
        <div className="grid grid-cols-[1fr_1.5fr_1fr_1fr_1.5fr_1fr_1fr_auto] gap-4 border-b border-slate-200 bg-slate-50 px-6 py-4 text-xs font-bold uppercase tracking-wider text-slate-500" data-id="element-707" data-mp="c1ruutxfbjld">
          <div data-id="element-708" data-mp="lxmmgejmkp0a">Order ID</div><div data-id="element-709" data-mp="lx32ytebtsk5">Customer</div><div data-id="element-710" data-mp="m0qw3rfv0cyc">Source</div><div data-id="element-711" data-mp="zyfgyz9xfk3e">Status</div><div data-id="element-712" data-mp="zxxbdc1djtit">Fulfillment</div><div data-id="element-713" data-mp="zxdt1u7tb349">Acceptance SLA</div><div data-id="element-714" data-mp="zwum18jp9z50">Amount / Payer</div><div className="w-10" data-id="element-715" data-mp="zw2o3r2mrpbk" />
        </div>
        <div className="divide-y divide-slate-100" data-id="element-716" data-mp="uyay52ljqda0">
          {filteredOrders.map(order => {
          const profile = deriveFulfillmentProfile(order);
          return <button key={order.id} onClick={() => onSelectOrder(order)} className={cn('group grid w-full grid-cols-[1fr_1.5fr_1fr_1fr_1.5fr_1fr_1fr_auto] items-center gap-4 px-6 py-5 text-left transition-colors', order.slaMinutes < 10 ? 'bg-rose-50/50 hover:bg-rose-50' : 'hover:bg-slate-50')} data-id="element-717" data-mp="14om2urd20qhy">
                <div className="font-mono font-bold text-blue-600" data-id="element-718" data-mp="1xb3zb69feg4r">#{order.id}</div>
                <div data-id="element-719" data-mp="22aq9t7xvdi9v"><p className="font-semibold text-slate-900" data-id="element-720" data-mp="22agkznok9h5r">{order.customer.name}</p><p className="text-xs text-slate-500" data-id="element-721" data-mp="228mal3ax17fx">{order.lines.length} items · {order.customer.mobile}</p></div>
                <div className="space-y-1" data-id="element-722" data-mp="1oda1trzxdgg0"><SourceBadge channel={order.channel} data-id="element-723" data-mp="1t9ip5soa0fy9" /><p className="text-[10px] font-bold uppercase text-slate-400" data-id="element-724" data-mp="1t8h18nm5vyz4">{order.channelPartner || order.payerType}</p></div>
                <StatusBadge status={order.status} data-id="element-725" data-mp="1ejb5xaqg2swi" />
                <div className="space-y-1" data-id="element-726" data-mp="1jcw4b3vy9ow8"><p className="text-[10px] font-bold uppercase text-slate-600" data-id="element-727" data-mp="1jd5no8mhoxsy">{profile.kind}</p>{profile.usesPartnerCourier && <p className="text-[9px] font-medium text-slate-400" data-id="element-728" data-mp="15id14q86mhcv">System partner dispatch</p>}</div>
                <div data-id="element-729" data-mp="19jygci86v7q8">
                  {order.status === 'Pending' ? <AcceptanceCountdown deadline={order.acceptanceDeadline} now={now} data-id="element-730" data-mp="vms2n22bnz7v" /> : <div className={cn('inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-[10px] font-bold uppercase', order.slaMinutes < 10 ? 'bg-red-50 text-red-700' : order.slaMinutes < 20 ? 'bg-amber-50 text-amber-700' : 'bg-emerald-50 text-emerald-700')} data-id="element-731" data-mp="10klby3yx99cd"><Clock className="h-3 w-3" data-id="element-732" data-mp="1u5xqgzka0bhp" />{order.slaMinutes}m</div>}
                </div>
                <div data-id="element-733" data-mp="1p6bbakkw0l69"><p className="font-bold text-slate-900" data-id="element-734" data-mp="1p6b708dutz0g">{order.totalAmount.toFixed(2)} SAR</p><PayerBadge payerType={order.payerType} data-id="element-735" data-mp="1k8t0spr81xw5" /></div>
                <div className="flex items-center gap-3" data-id="element-736" data-mp="1k5x0iurwix4j">{order.prescription && <ShieldCheck className={cn('h-4 w-4', order.prescription.verified ? 'text-emerald-500' : 'text-purple-500')} title={order.prescription.verified ? 'eRx verified' : 'eRx review required'} data-id="element-737" data-mp="1f8f23ybig0as" />}<ChevronRight className="h-5 w-5 text-slate-300 transition-colors group-hover:text-blue-600" data-id="element-738" data-mp="169jt6eh30c3s" /></div>
              </button>;
        })}
        </div>
      </div>
    </div>;
}
function SourceBadge({
  channel
}: {
  channel: Channel;
}) {
  const isDigital = channel === 'Website' || channel === 'Yaser eRx' || channel === 'Insurance Platform';
  return <div className={cn('inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-[10px] font-bold uppercase', isDigital ? 'bg-indigo-50 text-indigo-700' : channel === 'Walk-in' ? 'bg-purple-50 text-purple-700' : 'bg-orange-50 text-orange-700')} data-id="element-739" data-mp="3d4vl9fqe115g">{isDigital ? <Globe className="h-3 w-3" data-id="element-740" data-mp="3mu5bsr5l2brg" /> : channel === 'Walk-in' ? <User className="h-3 w-3" data-id="element-741" data-mp="2p7py80ga1y96" /> : <Smartphone className="h-3 w-3" data-id="element-742" data-mp="2p8hocvr3v3s2" />}{channel}</div>;
}
function PayerBadge({
  payerType
}: {
  payerType: PayerType;
}) {
  const labels: Record<PayerType, string> = {
    Cash: 'Cash',
    Insurance: 'Insurance',
    'Partner Prepaid': 'Partner',
    Deferred: 'Deferred'
  };
  const styles: Record<PayerType, string> = {
    Cash: 'bg-emerald-50 text-emerald-700 border-emerald-100',
    Insurance: 'bg-violet-50 text-violet-700 border-violet-100',
    'Partner Prepaid': 'bg-sky-50 text-sky-700 border-sky-100',
    Deferred: 'bg-amber-50 text-amber-700 border-amber-100'
  };
  return <span className={cn('mt-1 inline-flex rounded-full border px-2 py-0.5 text-[9px] font-black uppercase tracking-wide', styles[payerType])} data-id="element-743" data-mp="2fgbgfoxvapjb">{labels[payerType]}</span>;
}
function AcceptanceCountdown({
  deadline,
  now
}: {
  deadline: string;
  now: number;
}) {
  const remainingSeconds = Math.max(0, Math.floor((new Date(deadline).getTime() - now) / 1000));
  const hours = Math.floor(remainingSeconds / 3600);
  const minutes = Math.floor(remainingSeconds % 3600 / 60);
  const seconds = remainingSeconds % 60;
  const isExpired = remainingSeconds === 0;
  const label = isExpired ? 'Acceptance overdue' : `${hours > 0 ? `${hours}h ` : ''}${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  return <div className={cn('inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-[10px] font-bold uppercase tabular-nums', isExpired ? 'bg-red-50 text-red-700' : remainingSeconds < 600 ? 'bg-amber-50 text-amber-700' : 'bg-blue-50 text-blue-700')} data-id="element-744" data-mp="20qx5wdik222c"><Clock className="h-3 w-3" data-id="element-745" data-mp="3jr624mun3ne1" />{label}</div>;
}
export function StatusBadge({
  status
}: {
  status: OrderStatus;
}) {
  const styles: Record<OrderStatus, string> = {
    Pending: 'bg-blue-50 text-blue-700 border-blue-100',
    Accepted: 'bg-indigo-50 text-indigo-700 border-indigo-100',
    Picking: 'bg-amber-50 text-amber-700 border-amber-100',
    'Searching for Driver': 'bg-sky-50 text-sky-700 border-sky-100',
    'Awaiting Partner Courier': 'bg-cyan-50 text-cyan-700 border-cyan-100',
    'Driver Assigned': 'bg-violet-50 text-violet-700 border-violet-100',
    'Out for Delivery': 'bg-orange-50 text-orange-700 border-orange-100',
    Delivered: 'bg-emerald-50 text-emerald-700 border-emerald-100',
    'Delivery Attempt Failed': 'bg-rose-50 text-rose-700 border-rose-100',
    Held: 'bg-slate-100 text-slate-700 border-slate-200',
    Cancelled: 'bg-red-50 text-red-700 border-red-100',
    Rejected: 'bg-red-50 text-red-700 border-red-100',
    Reassigned: 'bg-fuchsia-50 text-fuchsia-700 border-fuchsia-100',
    'Awaiting Payment': 'bg-amber-50 text-amber-700 border-amber-100',
    'Ready for Pickup': 'bg-purple-50 text-purple-700 border-purple-100',
    'Return Requested': 'bg-rose-50 text-rose-700 border-rose-100',
    'Waiting Return Confirmation': 'bg-amber-50 text-amber-700 border-amber-100',
    Refunded: 'bg-slate-50 text-slate-700 border-slate-100'
  };
  const icons: Record<OrderStatus, React.ElementType> = {
    Pending: AlertCircle,
    Accepted: CheckCircle2,
    Picking: Package,
    'Searching for Driver': Truck,
    'Awaiting Partner Courier': Clock,
    'Driver Assigned': User,
    'Out for Delivery': Truck,
    Delivered: CheckCircle2,
    'Delivery Attempt Failed': AlertCircle,
    Held: Clock,
    Cancelled: XCircle,
    Rejected: XCircle,
    Reassigned: ChevronRight,
    'Awaiting Payment': ShieldCheck,
    'Ready for Pickup': Package,
    'Return Requested': AlertCircle,
    'Waiting Return Confirmation': Clock,
    Refunded: CheckCircle2
  };
  const Icon = icons[status];
  return <div className={cn('inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[10px] font-bold uppercase', styles[status])} data-id="element-746" data-mp="f9blf3apx5js"><Icon className="h-3 w-3" data-id="element-747" data-mp="nir130ninter" />{status}</div>;
}